import { useSession } from '@/composables/session';
import { createRouter, createWebHistory } from 'vue-router';
import InfoService from '@/services/infoService';

const infoService = new InfoService();

export function getFirstAccessibleRoute(perms: any) {
    // Order of preference. Adjust sequence as needed.
    const ordered = [
        { name: 'dashboard', allowed: () => perms?.hasAccessToDashboard },
        { name: 'order', allowed: () => perms?.hasAccessToOrderItemsTab },
        { name: 'cart', allowed: () => perms?.hasAccessToCartTab },
        { name: 'documents', allowed: () => perms?.hasAccessToDocumentsTab },
        { name: 'orders', allowed: () => perms?.hasAccessToPreviousOrdersTab },
        { name: 'downloads', allowed: () => perms?.hasAccessToDownload },
        { name: 'accountDebitors', allowed: () => perms?.hasAccessToMyDebitorsTab }
    ];
    const first = ordered.find(x => x.allowed());
    return first ? { name: first.name } : { name: 'loginForm' };
}

export function isRoutePermited(to: any, perms: any) {
    if (to.meta.requiresDashboardPermission && !perms?.hasAccessToDashboard) return false;
    if (to.meta.requiresOrderItemsPermission && !perms?.hasAccessToOrderItemsTab) return false;
    if (to.meta.requiresCartPermission && !perms?.hasAccessToCartTab) return false;
    if (to.meta.requiresDocumentsPermission && !perms?.hasAccessToDocumentsTab) return false;
    if (to.meta.requiresReOrderItemsPermission && !perms?.hasAccessToPreviousOrdersTab) return false;
    if (to.meta.requiresDownloadPermission && !perms?.hasAccessToDownload) return false;
    if (to.meta.requiresDebitorsPermission && !perms?.hasAccessToMyDebitorsTab) return false;
    return true;
}

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    linkActiveClass: 'active',
    linkExactActiveClass: 'exact-active',
    routes: [
        {
            path: '/',
            name: 'home',
            meta: { requiresAuth: true, requiresCustomer: true },
            component: () => import('@/views/DashboardView.vue')
        },
        {
            path: '/admin',
            name: 'admin',
            redirect: '/admin/impersonate-customer',
            meta: { requiresAuth: true, requiresAdmin: true },
            component: () => import('@/views/AdminView.vue'),
            children: [
                {
                    path: 'invite-users',
                    name: 'inviteUsers',
                    meta: { requiresAuth: true, requiresAdmin: true },
                    component: () => import('@/partials/AdminInviteUsers.vue')
                },
                {
                    path: 'impersonate-customer',
                    name: 'impersonateCustomer',
                    meta: { requiresAuth: true, requiresAdmin: true },
                    component: () => import('@/partials/AdminImpersonateCustomer.vue')
                }
            ]
        },
        {
            path: '/account',
            name: 'account',
            meta: { requiresAuth: true, requiresCustomer: true },
            component: () => import('@/views/AccountView.vue'),
            children: [
                {
                    path: '',
                    name: 'accountDetails',
                    meta: { requiresAuth: true, requiresCustomer: true },
                    component: () => import('@/partials/AccountDetailsPartial.vue')
                },
                {
                    path: 'change-password',
                    name: 'accountChangePassword',
                    meta: { requiresAuth: true, requiresCustomer: true },
                    component: () => import('@/partials/AccountChangePasswordPartial.vue')
                },
                {
                    path: 'debitors',
                    name: 'accountDebitors',
                    meta: { requiresAuth: true, requiresCustomer: true, requiresDebitorsPermission: true },
                    component: () => import('@/partials/AccountDebitorsPartial.vue')
                },
                {
                    path: 'debitors/edit/:id/:addressId',
                    name: 'accountDebitorsEdit',
                    meta: { requiresAuth: true, requiresCustomer: true, requiresDebitorsPermission: true },
                    component: () => import('@/partials/AccountDebitorsEditPartial.vue')
                },
                {
                    path: 'administrate-users',
                    name: 'administrateUsers',
                    meta: { requiresAuth: true, requiresCustomer: true },
                    component: () => import('@/partials/AdministrateCustomersRelatedToDebitors.vue')
                }
            ]
        },
        {
            path: '/cart',
            name: 'cart',
            meta: { requiresAuth: true, requiresCustomer: true, requiresCartPermission: true },
            component: () => import('@/views/CartView.vue')
        },
        {
            path: '/login',
            name: 'login',
            props: route => ({ query: route.query }),
            redirect: to => ({
                name: 'loginForm',
                query: to.query
            }),
            component: () => import('@/views/LoginView.vue'),
            children: [
                {
                    path: '',
                    name: 'loginForm',
                    props: route => ({ query: route.query }),
                    component: () => import('@/partials/LoginFormPartial.vue')
                },
                {
                    path: 'ad',
                    name: 'loginAd',
                    component: () => import('@/partials/LoginAdPartial.vue')
                },
                {
                    path: 'forgot-password',
                    name: 'forgotPassword',
                    component: () => import('@/partials/LoginForgotPasswordPartial.vue')
                },
                {
                    path: 'reset-password',
                    name: 'resetPassword',
                    component: () => import('@/partials/LoginConfirmPasswordPartial.vue')
                },
                {
                    path: 'choose-account',
                    name: 'chooseAccount',
                    meta: { requiresAuth: true, requiresCustomer: true },
                    component: () => import('@/partials/LoginChooseAccountPartial.vue')
                }
            ]
        },
        {
            path: '/dashboard',
            name: 'dashboard',
            meta: { requiresAuth: true, requiresCustomer: true, requiresDashboardPermission: true },
            component: () => import('@/views/DashboardView.vue')
        },
        {
            path: '/order',
            name: 'order',
            meta: { requiresAuth: true, requiresCustomer: true, requiresOrderItemsPermission: true },
            component: () => import('@/views/OrderView.vue')
        },
        {
            path: '/documents',
            name: 'documents',
            meta: { requiresAuth: true, requiresCustomer: true, requiresDocumentsPermission: true },
            component: () => import('@/views/DocumentsView.vue')
        },
        {
            path: '/orders',
            name: 'orders',
            meta: { requiresAuth: true, requiresCustomer: true, requiresReOrderItemsPermission: true },
            component: () => import('@/views/OrdersView.vue')
        },
        {
            path: '/download',
            name: 'downloads',
            meta: { requiresAuth: true, requiresCustomer: true, requiresDownloadPermission: true },
            component: () => import('@/views/DownloadView.vue')
        },
        {
            path: '/:pathMatch(.*)*',
            name: 'notFound',
            // Empty component so router-view doesn't render anything for Umbraco pages.
            component: { render: () => null },
            meta: { requiresAuth: true, requiresCustomer: true }
        }
    ],
    scrollBehavior(to, from, savedPosition) {
        // Always scroll to top
        return { top: 0 };
    },
});

router.afterEach(() => {
    window.scrollTo(0, 0);
});

router.beforeEach(async (to) => {
    const { currentUser, currentDebitor, clearSession } = useSession();

    console.debug(`[router] navigating to: "${String(to.name)}" (${to.path}) | currentUser: ${currentUser.value?.email ?? 'none'} | currentDebitor: ${currentDebitor.value?.debitorNumber ?? 'none'}`);

    // choose-account is only valid for authenticated users who have not yet selected a debitor.
    if (to.name === 'chooseAccount') {
        if (!currentUser.value) {
            console.debug('[router] chooseAccount — no user, redirecting to loginForm');
            return { name: 'loginForm' };
        }
        if (currentUser.value.isAdmin) {
            console.debug('[router] chooseAccount — admin user, redirecting to admin');
            return { name: 'admin' };
        }
        if (currentDebitor.value) {
            console.debug('[router] chooseAccount — debitor already set, redirecting into app');
            return getFirstAccessibleRoute(currentDebitor.value.debitorPermissions);
        }
        console.debug('[router] chooseAccount — authenticated, no debitor, showing picker');
        return true;
    }

    // 1. Authentication Check
    if (to.meta.requiresAuth) {
        if (!currentUser.value) {
            console.debug('[router] requiresAuth — no user, redirecting to loginForm');
            return {
                name: 'loginForm',
                query: {
                    ...to.query,
                    redirect: to.path !== '/' ? to.path : undefined
                }
            };
        }

        try {
            await infoService.Validate();
            console.debug('[router] Validate() — session OK');
        } catch (error: any) {
            if (error.response && error.response.status === 401) {
                console.debug('[router] Validate() — 401, clearing session');
                clearSession();
                localStorage.removeItem("store");

                return {
                    name: 'loginForm',
                    query: { force: "true", redirect: to.path !== '/' ? to.path : undefined }
                };
            }
        }
    }

    // 2. Customer/Admin context check
    if (to.meta.requiresAdmin && !currentUser.value?.isAdmin) {
        console.debug('[router] requiresAdmin — non-admin user, redirecting away from admin route');
        if (!currentDebitor.value) {
            return { name: 'chooseAccount' };
        }
        return getFirstAccessibleRoute(currentDebitor.value.debitorPermissions);
    }

    if (to.meta.requiresCustomer) {
        if (currentUser.value?.isAdmin) {
            console.debug('[router] requiresCustomer — admin user, redirecting to admin');
            return { name: 'admin' };
        }
        else if (!currentDebitor.value) {
            console.debug('[router] requiresCustomer — no debitor selected, redirecting to chooseAccount');
            return { name: 'chooseAccount' };
        }
    }

    const perms = currentDebitor.value?.debitorPermissions;

    // 3. Dynamic Landing Page Decision
    if (to.name === 'notFound' || to.path === '/' || !isRoutePermited(to, perms)) {
        if (to.name === 'notFound' && to.path !== '/') {
            return true;
        }
        const landing = getFirstAccessibleRoute(perms);
        console.debug('[router] Landing page decision — routing to:', landing);
        return landing;
    }
});

export default router;
